import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { can } from "@/lib/permissions";
import { PageHeader, InfoNote } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const COLOR_DOT = { GREEN: "bg-emerald-500", YELLOW: "bg-amber-500", REVIEW: "bg-slate-400", RED: "bg-rose-500" };
const ISSUE_BY_STATUS = { REVIEW_REQUIRED: "Uncertain / unsupported", ACTION_NEEDED: "Missing evidence", READY: "Confirmed" };
function ageOf(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return "<1h";
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function HumanReview() {
  const { roleCode } = useApp();
  const nav = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [ovOpen, setOvOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [ov, setOv] = useState({ new_status: "READY", reason_category: "Documented clinical judgment", justification: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.reviews().then(setReviews).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const open = reviews.filter((r) => r.state === "OPEN");

  const resolve = async (r, action) => {
    setBusy(true);
    try { await api.resolveReview(r.id, { role_code: roleCode, action }); toast.success("Review updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const submitOverride = async () => {
    if (!ov.justification) return toast.error("Justification is required");
    setBusy(true);
    try {
      await api.resolveReview(target.id, { role_code: roleCode, action: "override", ...ov });
      toast.success("Override applied — original decision retained"); setOvOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader testid="human-review-header" title="Human Review Queue"
        subtitle="Ambiguous, contradictory, low-confidence or unsupported cases routed for human judgment. Uncertainty is a valid outcome." />
      {!can.resolveReview(roleCode) && (
        <InfoNote className="mb-4">Your current persona can view the review queue but only a Revenue / Compliance Reviewer or System Administrator may resolve or override.</InfoNote>
      )}

      {open.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">No open reviews.</Card>
      ) : (
        <Card className="overflow-hidden" data-testid="review-queue">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Case / Order</TableHead>
                <TableHead>Review reason</TableHead>
                <TableHead>Readiness</TableHead>
                <TableHead>Evidence issue</TableHead>
                <TableHead>Age</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {open.map((r) => (
                <TableRow key={r.id} data-testid={`review-${r.id}`} className="align-top">
                  <TableCell>
                    <div className="font-display text-sm font-semibold text-slate-900">{r.order?.patient_name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{r.order?.order_number}</div>
                    <div className="text-[11px] text-slate-500">{r.order?.test_name}</div>
                    {r.auto_opened && <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">auto-opened</span>}
                  </TableCell>
                  <TableCell className="max-w-[260px] text-xs text-slate-600">{r.reason}</TableCell>
                  <TableCell><StatusBadge status={r.order?.status} size="sm" /></TableCell>
                  <TableCell className="text-xs">
                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                      <span className={cn("h-2 w-2 rounded-full", COLOR_DOT[r.order?.readiness_color] || "bg-slate-300")} />
                      {ISSUE_BY_STATUS[r.order?.status] || "Review"}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{ageOf(r.created_at)}</TableCell>
                  <TableCell className="text-xs text-slate-600">Revenue / Compliance reviewer</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Button size="sm" className="h-7 bg-teal-600 px-2 text-xs hover:bg-teal-700" data-testid={`review-open-order-${r.id}`} onClick={() => nav(`/orders/${r.order_id}`)}>Open workspace</Button>
                      {can.resolveReview(roleCode) && <>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid={`review-confirm-${r.id}`} disabled={busy} onClick={() => resolve(r, "confirm")}>Confirm</Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid={`review-clarify-${r.id}`} disabled={busy} onClick={() => resolve(r, "clarify")}>Clarify</Button>
                        <Button size="sm" className="h-7 bg-slate-900 px-2 text-xs hover:bg-slate-800" data-testid={`review-override-${r.id}`} onClick={() => { setTarget(r); setOvOpen(true); }}>Override</Button>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={ovOpen} onOpenChange={setOvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Override Readiness Status</DialogTitle>
            <DialogDescription>The original automated decision is always retained. Override requires a documented justification.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">New status</Label>
              <Select value={ov.new_status} onValueChange={(v) => setOv({ ...ov, new_status: v })}>
                <SelectTrigger data-testid="ov-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="READY">READY</SelectItem>
                  <SelectItem value="ACTION_NEEDED">ACTION NEEDED</SelectItem>
                  <SelectItem value="REVIEW_REQUIRED">REVIEW REQUIRED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Reason category</Label>
              <Select value={ov.reason_category} onValueChange={(v) => setOv({ ...ov, reason_category: v })}>
                <SelectTrigger data-testid="ov-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Documented clinical judgment">Documented clinical judgment</SelectItem>
                  <SelectItem value="Additional record reviewed">Additional record reviewed</SelectItem>
                  <SelectItem value="Policy interpretation">Policy interpretation</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Justification</Label>
              <Textarea data-testid="ov-justification" rows={3} value={ov.justification} onChange={(e) => setOv({ ...ov, justification: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="ov-submit" className="bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={submitOverride}>Apply Override</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
